import React, { useEffect, useState } from "react";
import { Observer, useLocalStore } from "mobx-react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Paper
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Add, Delete, Edit } from "@material-ui/icons";
import moment from "moment";
import { getProfessionDisplayName } from "@bonliva-crm/common/data/professions";
import { ProfessionsByCountry } from "@graphql";
import { store } from "@store";
import { TgRequirementFragment } from "@graphql";
import { TgRequirementSearchStore } from "./tg-requirement-search-store";
import { TgRequirementEditDialog } from "./tg-requirement-edit-dialog";

function isActive(item: TgRequirementFragment) {
  if (!item.validTo) return true;
  return new Date(item.validTo) > new Date();
}

export function TgRequirementsSearch() {
  const localStore = useLocalStore(() => new TgRequirementSearchStore());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [professionGroups, setProfessionGroups] = useState<
    { country: string; professions: string[] }[]
  >([]);

  const canEdit = store().user.can("config-editor:update:all");

  useEffect(() => {
    ProfessionsByCountry.func({}).then((result) => {
      setProfessionGroups(result.data.professionsByCountry);
    });
  }, []);

  const handleCreate = (data: {
    costCenterId: string;
    profession: string | null;
    minRequiredTG: number;
  }) => {
    localStore.create(data);
  };

  const handleUpdate = (data: {
    costCenterId: string;
    profession: string | null;
    minRequiredTG: number;
  }) => {
    if (editItem) {
      localStore.update(editItem._id, data.minRequiredTG);
    }
  };

  const handleDelete = (id: string) => {
    localStore.delete(id);
  };

  const openCreateDialog = () => {
    setEditItem(null);
    setDialogOpen(true);
  };

  const openEditDialog = (item: any) => {
    setEditItem(item);
    setDialogOpen(true);
  };

  return (
    <Observer>
      {() => (
        <div style={{ padding: 16, flex: 1, maxWidth: 1200, margin: "0 auto", width: "100%" }}>
          <Toolbar disableGutters>
            <Typography variant="h6" style={{ flex: 1 }}>
              TG-krav
            </Typography>
            <TextField
              label="Filtrera kostnadsställe"
              value={localStore.costCenterFilter}
              onChange={(e) => localStore.setCostCenterFilter(e.target.value)}
              size="small"
              variant="outlined"
              style={{ marginRight: 16 }}
            />
            <Autocomplete<{ value: string; label: string; group: string }>
              options={[
                { value: "__standard__", label: "Standard", group: "" },
                ...professionGroups.flatMap((group) =>
                  [...group.professions]
                    .sort((a, b) =>
                      getProfessionDisplayName(a).localeCompare(
                        getProfessionDisplayName(b),
                        "sv"
                      )
                    )
                    .map((p) => ({
                      value: p,
                      label: getProfessionDisplayName(p),
                      group: group.country
                    }))
                )
              ]}
              getOptionLabel={(option) => option.label}
              groupBy={(option) => option.group}
              value={
                localStore.professionFilter
                  ? localStore.professionFilter === "__standard__"
                    ? { value: "__standard__", label: "Standard", group: "" }
                    : {
                        value: localStore.professionFilter,
                        label: getProfessionDisplayName(localStore.professionFilter),
                        group: ""
                      }
                  : null
              }
              onChange={(_, newValue) => {
                const val = typeof newValue === "string" ? newValue : newValue?.value ?? "";
                localStore.setProfessionFilter(val);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filtrera profession"
                  size="small"
                  variant="outlined"
                />
              )}
              style={{ marginRight: 16, minWidth: 200 }}
              size="small"
            />
            {canEdit && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={openCreateDialog}
              >
                Lägg till
              </Button>
            )}
          </Toolbar>

          <Paper style={{ marginTop: 16 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kostnadsställe</TableCell>
                  <TableCell>Profession</TableCell>
                  <TableCell>Min TG (%)</TableCell>
                  <TableCell>Giltig från</TableCell>
                  <TableCell>Giltig till</TableCell>
                  <TableCell>Skapad av</TableCell>
                  {canEdit && <TableCell align="right">Åtgärder</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {localStore.sortedItems.map((item) => {
                  const active = isActive(item);
                  return (
                    <TableRow
                      key={item._id}
                      style={{
                        backgroundColor: active ? "#e8f5e9" : "#f5f5f5"
                      }}
                    >
                      <TableCell>{item.costCenterId}</TableCell>
                      <TableCell>{item.profession || "Standard"}</TableCell>
                      <TableCell>{item.minRequiredTG}</TableCell>
                      <TableCell>
                        {moment(item.validFrom).format("YYYY-MM-DD")}
                      </TableCell>
                      <TableCell>
                        {item.validTo
                          ? moment(item.validTo).format("YYYY-MM-DD")
                          : "–"}
                      </TableCell>
                      <TableCell>{item.createdByName}</TableCell>
                      {canEdit && (
                        <TableCell align="right">
                          {active && (
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(item)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                          {active && (
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(item._id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {localStore.sortedItems.length === 0 && !localStore.loading && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} align="center">
                      Inga TG-krav hittades
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <TgRequirementEditDialog
            open={dialogOpen}
            onClose={() => {
              setDialogOpen(false);
              localStore.fetch();
            }}
            onSubmit={editItem ? handleUpdate : handleCreate}
            initialValues={
              editItem
                ? {
                    costCenterId: editItem.costCenterId,
                    profession: editItem.profession,
                    minRequiredTG: editItem.minRequiredTG
                  }
                : undefined
            }
            isEdit={!!editItem}
          />
        </div>
      )}
    </Observer>
  );
}